import {Inject, Injectable} from "@angular/core";
import {countries} from "countries-list"
import {ENVIRONMENT, IS_BROWSER} from "../utils/injectiontokens";

@Injectable({
  providedIn: 'root'
})
export class I18nServices {

  protected languageIsoCode: string;
  protected countryIsoCode: string;
  protected supportedLanguages = ['es', 'en'];

  //regarding how to parse numbers:
  protected _group: RegExp;
  protected _decimal: RegExp;
  protected _numeral: RegExp;
  protected _index: any;

  public constructor(@Inject(IS_BROWSER) protected isBrowser: boolean,
                     @Inject(ENVIRONMENT) protected environment: any) {

    let locale = this.environment.defaultLanguageIsoCode + '-' + this.environment.defaultCountryIsoCode; //default locale
    if(isBrowser && navigator.language != null) {
      locale = navigator.language;
    }
    this.setLocaleIsoCode(locale);
  }

  public setLocaleIsoCode(locale: string) {
    let originalLanguageIsoCode = null;
    let localeParts = locale.split("-");
    if (localeParts.length == 2) {
      this.languageIsoCode = localeParts[0];
      originalLanguageIsoCode = this.languageIsoCode;
      this.countryIsoCode = localeParts[1];
    }
    else if(localeParts.length == 1) { //just language, let use default country for language
      this.languageIsoCode = localeParts[0];
      originalLanguageIsoCode = this.languageIsoCode;
    }

    //if language is not supported, then use default supported language (english)
    if(this.supportedLanguages.indexOf(this.languageIsoCode) == -1) {
      this.languageIsoCode = this.environment.defaultLanguageIsoCode;
    }

    if(this.countryIsoCode == null) {
      this.countryIsoCode = this.getDefaultCountryForLanguage(originalLanguageIsoCode);
      if(this.countryIsoCode == null) {
        if(originalLanguageIsoCode != this.languageIsoCode) {
          this.countryIsoCode = this.getDefaultCountryForLanguage(this.languageIsoCode);
        }
        if(this.countryIsoCode == null) {
          this.countryIsoCode = this.environment.defaultCountryIsoCode;
        }
      }
    }

    // @ts-ignore
    const numberParts = new Intl.NumberFormat(locale).formatToParts(12345.6);

    // @ts-ignore
    const numerals = [...new Intl.NumberFormat(locale, {useGrouping: false}).format(9876543210)].reverse();
    const index = new Map(numerals.map((d, i) => [d, i]));
    this._group = new RegExp(`[${numberParts.find(d => d.type === "group").value}]`, "g");
    this._decimal = new RegExp(`[${numberParts.find(d => d.type === "decimal").value}]`);
    this._numeral = new RegExp(`[${numerals.join("")}]`, "g");
    this._index = d => index.get(d);
  }

  public getLocaleIsoCode() {
    let returnValue = this.languageIsoCode.toLowerCase();
    if(this.countryIsoCode != null) {
      returnValue = returnValue + '-' + this.countryIsoCode.toUpperCase();
    }
    return returnValue;
  }

  public getLanguageIsoCode() {
    let returnValue = null;
    if(this.languageIsoCode != null) {
      returnValue = this.languageIsoCode.toLowerCase();
    }
    return returnValue;
  }

  public setLanguageIsoCode(languageIsoCode: string) {
    if(this.countryIsoCode != null) {
      this.setLocaleIsoCode(languageIsoCode + '-' + this.countryIsoCode);
    }
    else {
      this.setLocaleIsoCode(languageIsoCode);
    }
  }

  public getCountryIsoCode() {
    let returnValue = null;
    if(this.countryIsoCode != null) {
      returnValue = this.countryIsoCode.toUpperCase();
    }
    return returnValue;
  }

  public setCountryIsoCode(countryIsoCode: string) {
    if(this.languageIsoCode != null) {
      this.setLocaleIsoCode(this.languageIsoCode + '-' + countryIsoCode);
    }
    else {
      this.setLocaleIsoCode(countryIsoCode);
    }
  }

  public getDefaultCountryForLanguage(languageIsoCode: string) {
    let returnValue = null;
    switch(languageIsoCode) {
      case 'es':
        returnValue = 'ES';
        break;
      case 'en':
        returnValue = 'US';
        break;
      case 'fr':
        returnValue = 'FR';
        break;
      case 'de':
        returnValue = 'DE';
        break;
      case 'it':
        returnValue = 'IT';
        break;
      case 'pt':
        returnValue = 'PT';
        break;
    }
    if(returnValue == null) {
      for (let code in countries) {
        let entry = countries[code];
        if (entry.languages.indexOf(languageIsoCode) != -1) {
          returnValue = code;
          break;
        }
      }
    }
    return returnValue;
  }

  public formatCurrency(number: number, currency: string, showDecimals: boolean = true, useCompactNotation = true): string {
    let options: any = { style: 'currency', currency: currency };
    if(!showDecimals) {
      options['minimumFractionDigits'] = 0;
      options['maximumFractionDigits'] = 0;
    }
    else {
      if (number > 0 && number < 0.01) {
        let precision = 2;
        while (precision < 12 && number < 1000 / Math.pow(10, precision)) {
          precision++;
        }
        options['minimumFractionDigits'] = precision;
        options['maximumFractionDigits'] = precision;
      }
    }
    if(useCompactNotation) {
      options = this.getOptionsForCompactNotation(number, options);
    }

    let locale = this.getLocaleIsoCode();
    return new Intl.NumberFormat(locale, options).format(number);
  }

  public formatNumber(number: number, minDecimals = 0, maxDecimals = null, useCompactNotation = true, useGrouping = true): string {

    if(maxDecimals == null) {
      maxDecimals = minDecimals;
    }

    let options: any = { minimumFractionDigits: minDecimals, maximumFractionDigits: maxDecimals, useGrouping: useGrouping };
    if(useCompactNotation) {
      options = this.getOptionsForCompactNotation(number, options);
    }

    let locale = this.getLocaleIsoCode();
    return new Intl.NumberFormat(locale,options).format(number);
  }

  protected getOptionsForCompactNotation(number: number, options: any): any {
    if (number > 1000000) {
      options['compactDisplay'] = 'short';
      options['notation'] = 'compact';
      //show 2 decimals:
      let numberUnitsAmount = 1000000;
      if(number > 1000000000) {
        numberUnitsAmount = 1000000000;
      }
      let significantPart = Math.round(number / numberUnitsAmount);
      options['maximumSignificantDigits'] = significantPart.toString().length + 2;
    } else if (number > 1000) {
      options['minimumFractionDigits'] = 0;
      options['maximumFractionDigits'] = 0;
    }

    return options;
  }

  public parseNumber(number: string): number {
    if (number != null) {
      return (number = number.trim()
        .replace(this._group, "")
        .replace(this._decimal, ".")
        .replace(this._numeral, this._index)) ? +number : NaN;
    }
    return null;
  }

}
